if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.hideErrors = this.dataset.hideErrors === 'true';

        const variantInput = this.variantIdInput;
        if (variantInput) variantInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        // Mark this form as managed by product-form custom element
        // so homepassion-cart-ajax-fix.js can skip it
        this.form.dataset.productFormManaged = 'true';

        if (this.cart && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation(); // Prevent ajax-fix from also handling this

        if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.setLoading(true);

        try {
          const formData = new FormData(this.form);

          if (!formData.get('id')) {
            throw new Error('Lütfen bir varyant seçin.');
          }

          // Request section re-renders from Shopify
          formData.append('sections', 'cart-drawer,cart-icon-bubble');
          formData.append('sections_url', window.location.pathname || '/');

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/json',
            },
            body: formData,
          });

          const parsedState = await response.json();

          if (!response.ok || parsedState.status) {
            throw new Error(parsedState.description || parsedState.message || 'Ürün sepete eklenemedi.');
          }

          // parsedState.sections contains fresh HTML for cart-drawer and cart-icon-bubble
          this._renderCartFromSections(parsedState);

        } catch (error) {
          console.error('[product-form] Sepete ekleme hatası:', error);
          this.handleErrorMessage(error.message || 'Ürün sepete eklenirken hata oluştu.');
        } finally {
          this.setLoading(false);
        }
      }

      _renderCartFromSections(parsedState) {
        if (!parsedState || !parsedState.sections) {
          console.warn('[product-form] sections verisi yok, fallback refresh yapılıyor...');
          // Fallback: refresh separately
          this._fallbackRefresh();
          return;
        }

        const drawerHTML = parsedState.sections['cart-drawer'];
        const bubbleHTML = parsedState.sections['cart-icon-bubble'];
        const parser = new DOMParser();

        // --- Update cart-drawer ---
        if (drawerHTML) {
          const newDoc = parser.parseFromString(drawerHTML, 'text/html');
          const currentDrawer = document.querySelector('cart-drawer');
          const newDrawer = newDoc.querySelector('cart-drawer');
          const newDrawerInner = newDoc.querySelector('#CartDrawer');
          const currentDrawerInner = document.querySelector('#CartDrawer');

          if (currentDrawer && newDrawer) {
            // Replace inner HTML so the custom element instance stays alive
            currentDrawer.innerHTML = newDrawer.innerHTML;
            // Remove empty-state classes
            currentDrawer.classList.remove('is-empty');
            currentDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
            currentDrawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
            // Re-bind overlay since innerHTML was replaced
            if (typeof currentDrawer.bindOverlay === 'function') {
              currentDrawer.bindOverlay();
            }
          } else if (currentDrawerInner && newDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
            document.querySelector('cart-drawer')?.classList.remove('is-empty');
          }
        }

        // --- Update cart icon bubble ---
        if (bubbleHTML) {
          const newDoc = parser.parseFromString(bubbleHTML, 'text/html');
          const newBubble = newDoc.querySelector('#cart-icon-bubble');
          const currentBubble = document.querySelector('#cart-icon-bubble');
          if (newBubble && currentBubble) {
            currentBubble.innerHTML = newBubble.innerHTML;
          }
        }

        // --- Open the drawer ---
        this._openCartDrawer();
      }

      _openCartDrawer() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }

        cartDrawer.classList.remove('is-empty');
        cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');

        if (typeof cartDrawer.open === 'function') {
          setTimeout(() => cartDrawer.open(this.submitButton), 30);
        } else {
          cartDrawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
        }
      }

      async _fallbackRefresh() {
        try {
          const url = (window.routes?.cart_url || '/cart') +
            '?sections=cart-drawer,cart-icon-bubble&sections_url=' +
            encodeURIComponent(window.location.pathname);
          const resp = await fetch(url, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          const sections = await resp.json();
          this._renderCartFromSections({ sections });
        } catch (e) {
          console.error('[product-form] Fallback refresh hatası:', e);
        }
      }

      setLoading(isLoading) {
        const spinner = this.querySelector('.loading__spinner');

        if (!this.submitButton) return;

        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Sepete ekle';
        }
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
}